import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { employeeService } from '../services/service';
import type { Employee, Tag } from '../types/services';
import { useRbSystemAlert } from '@scmlatam/web-ui';
import { useTranslation } from 'react-i18next';
import { usePermisoEmpleado as usePermiso } from '../../../../auth/permisosModulos';
import { PermisosEmpleado } from '../../../../auth/permisosModulos';

const useToolbar = () => {
    const { showAlert } = useRbSystemAlert();
    const { t } = useTranslation('workers.table');
    const canViewTable = usePermiso('listado-empleados');

    const [period, setPeriod] = useState<[Dayjs | null, Dayjs | null]>([null, null]);

    const [employees, setEmployees] = useState<Employee[]>([]);

    const [tags, setTags] = useState<Tag[]>([]);
    const [tagSelected, setTagSelected] = useState<string[]>([]);
    const [searchTag, setSearchTag] = useState<string>('');

    const [filter, setFilter] = useState<string>('');

    const [loading, setLoading] = useState<boolean>(false);

    const initializeToolbar = async () => {
        setLoading(true);

        const searchParams = new URLSearchParams(window.location.search);

        const startDate = searchParams.get('start_date');
        const endDate = searchParams.get('end_date');
        const tagIds = searchParams.get('tags');

        const { tags } = await employeeService.getTags({
            params: {},
            alert: { showAlert, t },
        });

        setTags(tags);

        if (dayjs(startDate).isValid() && dayjs(endDate).isValid() && tagIds) {
            setPeriod([dayjs(startDate), dayjs(endDate)]);
            setTagSelected(tagIds.split(','));

            await search(startDate || '', endDate || '', tagIds || '');
        } else {
            setPeriod([dayjs().startOf('month'), dayjs().endOf('month')]);
            setTagSelected(tags.map((tag) => tag.value.toString()));

            await search(dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().endOf('month').format('YYYY-MM-DD'), tags.map((tag) => tag.value.toString()).join(','));
        }

        // asignar query params con valores por defecto
        const defaultStartDate = dayjs().startOf('month').format('YYYY-MM-DD');
        const defaultEndDate = dayjs().endOf('month').format('YYYY-MM-DD');

        const params = new URLSearchParams({
            module: 'timekeeper',
            view: 'workers',
            start_date: defaultStartDate,
            end_date: defaultEndDate,
            tags: tags.map((tag) => tag.value.toString()).join(','),
        });

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState(null, '', newUrl);

        setLoading(false);
    };

    const search = async (start_date: string, end_date: string, tags_id__in: string) => {
        setLoading(true);

        const { employees } = await employeeService.getEmployees({
            params: {
                start_date: start_date,
                end_date: end_date,
                tags_id__in: tags_id__in,
                without_hiring: true,
            },
            alert: { showAlert, t },
        });

        const params = new URLSearchParams({
            module: 'timekeeper',
            view: 'workers',
            start_date: start_date,
            end_date: end_date,
            tags: tags_id__in,
        });

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState(null, '', newUrl);

        setLoading(false);

        setEmployees(employees);
    };

    const reload = async () => {
        setLoading(true);

        const searchParams = new URLSearchParams(window.location.search);

        const startDate = searchParams.get('start_date');
        const endDate = searchParams.get('end_date');
        const tagIds = searchParams.get('tags');

        const { employees } = await employeeService.getEmployees({
            params: {
                start_date: startDate || '',
                end_date: endDate || '',
                tags_id__in: tagIds || '',
                without_hiring: true,
            },
            alert: { showAlert, t },
        });

        setLoading(false);

        setEmployees(employees);
    };

    useEffect(() => {
        if (canViewTable) initializeToolbar();
    }, [canViewTable]);

    return {
        canViewTable,
        requiredPermissions: PermisosEmpleado['listado-empleados'],
        period,
        setPeriod,
        employees,
        setEmployees,
        tags,
        setTags,
        tagSelected,
        setTagSelected,
        searchTag,
        setSearchTag,
        filter,
        setFilter,
        loading,
        setLoading,
        search,
        reload,
    };
};

export { useToolbar };
